/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import {
  getPresetItemQuantity,
  getPresetItemTotalPrice,
  getPresetSubTotalPrice,
  extractRecipientName,
} from 'utils/preset.util';

import { priceDecimalFormatter } from 'formatter/preset.formatter';
import { useSelector } from 'react-redux';
import OrderError from './OrderError';
import { Info, ShoppingBag, UserRound, X } from 'lucide-react';
import ReactTooltip from 'react-tooltip';

function CheckoutInfo({
  setCurrentPresetItemFromCheckoutData,
  showMinHourNoticeError,
  tooltipData,
  deliveryPriceCents,
  removeCheckoutItem,
  preOrderNoticeHour,
  cutleryFeesInCents,
  showAddondata,
  checkoutData,
  cutleryCount,
  cutleryFees,
  readOnly,
  preset,
}) {
  const isDelivery = useSelector(state => state.receivingMethod === 'delivery');
  const { presetItems } = preset;
  const subTotal = getPresetSubTotalPrice(checkoutData);
  const cutlerySubTotal = subTotal + cutleryFeesInCents;
  const RecipientNameLabel = ({ servingDetails: itemServingDetails }) => {
    const recipientName = extractRecipientName(itemServingDetails);
    if (!recipientName) return null;
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-1">
        <UserRound className="w-3 h-3 text-[#E53935]" />
        For <span className="font-semibold text-gray-700">{recipientName}</span>
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-5">
      {presetItems && presetItems.length > 0 && (
        <>
          {showMinHourNoticeError && (
            <div className="mb-3">
              <OrderError
                errorText={`This preset requires ${preOrderNoticeHour} hours notice, please select a delivery date and time that meets the minimum lead time`}
              />
            </div>
          )}

          <div className="space-y-3">
            {presetItems?.map(presetItem => {
              const presetItemCheckoutData = find(
                checkoutData,
                presetForm => presetForm.id === presetItem.id,
              );

              if (presetItemCheckoutData === undefined) return null;
              const {
                menuItems,
                menuItemsMapping,
                specialInstruction,
                servingDetails,
              } = presetItemCheckoutData;
              return (
                <div
                  className="pt-3 first:pt-0 border-t first:border-t-0 border-gray-100 cursor-pointer"
                  key={presetItem.id}
                  onClick={() => {
                    setCurrentPresetItemFromCheckoutData(
                      presetItemCheckoutData.id,
                    );
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {!menuItems?.length && (
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-panda-tint text-[#E53935] text-[11px] font-bold flex items-center justify-center">
                          {getPresetItemQuantity(presetItemCheckoutData)}
                        </span>
                      )}
                      <span className="font-heading font-semibold text-sm text-[#202124] truncate">
                        {presetItem.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-bold text-[#E53935]">
                        ${getPresetItemTotalPrice(presetItemCheckoutData) / 100}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          aria-label="Remove item"
                          className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#E53935] hover:bg-red-50 transition-colors"
                          onClick={e => {
                            e.stopPropagation();
                            removeCheckoutItem(presetItem.id);
                          }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <RecipientNameLabel servingDetails={servingDetails} />
                  {menuItems?.map(menuItem => {
                    if (
                      !menuItemsMapping[menuItem.id] ||
                      parseInt(menuItemsMapping[menuItem.id], 10) === 0
                    ) {
                      return null;
                    }
                    return (
                      <div
                        className="flex items-center gap-2 mt-1.5 ml-7"
                        key={menuItem.id}
                      >
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-panda-tint text-[#E53935] text-[11px] font-bold flex items-center justify-center">
                          {parseInt(menuItemsMapping[menuItem.id], 10)}
                        </span>
                        <span className="text-xs text-gray-600">{menuItem.name}</span>
                      </div>
                    );
                  })}

                  {specialInstruction && (
                    <p className="text-[11px] text-gray-500 italic mt-1.5">
                      <span className="font-semibold not-italic">Instructions: </span>
                      {specialInstruction}
                    </p>
                  )}
                </div>
              );
            })}
            {cutleryCount > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-panda-tint text-[#E53935] text-[11px] font-bold flex items-center justify-center">
                      {cutleryCount}
                    </span>
                    <span className="font-heading font-semibold text-sm text-[#202124]">
                      Cutlery/Plates/Napkins ($0.50/pc)
                    </span>
                  </div>
                  <span className="text-xs font-bold text-[#E53935]">
                    ${cutleryFees}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold text-[#202124]">
                $
                {priceDecimalFormatter(
                  cutleryFees ? cutlerySubTotal : subTotal,
                )}
              </span>
            </div>

            {!showAddondata && isDelivery && (
              <div className="flex justify-between text-xs text-gray-600">
                <span
                  className="inline-flex items-center gap-1 cursor-pointer"
                  data-tip={tooltipData}
                  data-for="delivery-tooltip"
                >
                  Delivery
                  <Info className="w-3.5 h-3.5" />
                </span>
                <ReactTooltip
                  id="delivery-tooltip"
                  place="right"
                  type="light"
                  effect="solid"
                  className="custom-tooltip"
                />
                <span className="font-semibold text-[#202124]">
                  ${priceDecimalFormatter(deliveryPriceCents)}
                </span>
              </div>
            )}

            <div className="flex justify-between text-xs text-gray-600">
              <span>
                {preset.taxName || 'HST'} ({(preset.taxRate * 100).toFixed(3)}%)
              </span>
              <span className="font-semibold text-[#202124]">
                $
                {priceDecimalFormatter(
                  (!showAddondata && isDelivery
                    ? (cutleryFees ? cutlerySubTotal : subTotal) +
                    deliveryPriceCents
                    : cutleryFees
                      ? cutlerySubTotal
                      : subTotal) * preset.taxRate,
                )}
              </span>
            </div>
          </div>
        </>
      )}
      {!presetItems ||
        (presetItems.length === 0 && (
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-2xl bg-panda-tint border-2 border-dashed border-red-200 flex items-center justify-center mb-3">
              <ShoppingBag className="w-7 h-7 text-[#E53935]" />
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              Your cart is empty!
              <br />
              Add items to get started.
            </p>
          </div>
        ))}
    </div>
  );
}

CheckoutInfo.propTypes = {
  preset: PropTypes.shape(),
  checkoutData: PropTypes.arrayOf(PropTypes.shape({})),
  deliveryPriceCents: PropTypes.number,
  setCurrentPresetItemFromCheckoutData: PropTypes.func,
  removeCheckoutItem: PropTypes.func,
  preOrderNoticeHour: PropTypes.number,
  showMinHourNoticeError: PropTypes.bool,
  readOnly: PropTypes.bool,
};

CheckoutInfo.defaultProps = {
  preset: {},
  checkoutData: [],
  deliveryPriceCents: 0,
  preOrderNoticeHour: 0,
  showMinHourNoticeError: false,
  readOnly: false,
  setCurrentPresetItemFromCheckoutData: () => { },
  removeCheckoutItem: () => { },
};
export default CheckoutInfo;
